import { X } from 'lucide-react';
import type { ReactNode } from 'react';
import { SWATCHES, type Swatch } from '@bccomms/shared';

export function Field({ label, help, children }: { label: string; help?: string; children: ReactNode }) {
  return (
    <label className="field">
      <span className="field__label">{label}</span>
      {children}
      {help && <span className="field__help">{help}</span>}
    </label>
  );
}

export function Toggle({ label, on, onChange, help }: { label: string; on: boolean; onChange: (v: boolean) => void; help?: string }) {
  return (
    <div className={`toggle ${on ? 'toggle--on' : ''}`} role="switch" aria-checked={on} tabIndex={0} onClick={() => onChange(!on)} onKeyDown={(e) => e.key === ' ' && onChange(!on)}>
      <span>
        {label}
        {help && <div className="field__help">{help}</div>}
      </span>
      <span className="toggle__track" />
    </div>
  );
}

export function Modal({ title, onClose, children, actions }: { title: string; onClose: () => void; children: ReactNode; actions?: ReactNode }) {
  return (
    <>
      <div className="backdrop" onClick={onClose} />
      <div className="modal" role="dialog" aria-label={title}>
        <div className="row row--between">
          <h3 className="modal__title">{title}</h3>
          <button className="iconbtn" aria-label="Close" onClick={onClose}>
            <X size={18} />
          </button>
        </div>
        {children}
        {actions && <div className="modal__actions">{actions}</div>}
      </div>
    </>
  );
}

export function Drawer({ title, onClose, children, head }: { title: string; onClose: () => void; children: ReactNode; head?: ReactNode }) {
  return (
    <>
      <div className="backdrop" onClick={onClose} />
      <aside className="drawer" role="dialog" aria-label={title}>
        <div className="drawer__head">
          <span className="drawer__title">{title}</span>
          <div className="row">
            {head}
            <button className="iconbtn" aria-label="Close" onClick={onClose}>
              <X size={18} />
            </button>
          </div>
        </div>
        {children}
      </aside>
    </>
  );
}

export function SwatchPicker({ value, onChange, allowNull }: { value: Swatch | null; onChange: (s: Swatch | null) => void; allowNull?: boolean }) {
  return (
    <div className="swatches">
      {allowNull && (
        <button type="button" className={`check ${value === null ? 'check--on' : ''}`} onClick={() => onChange(null)}>
          auto
        </button>
      )}
      {SWATCHES.map((s) => (
        <button key={s} type="button" data-sw={s} className={`swatch ${value === s ? 'swatch--on' : ''}`} aria-label={s} title={s} onClick={() => onChange(s)} />
      ))}
    </div>
  );
}

export function Chip({ kind, children, mono }: { kind?: 'success' | 'warning' | 'error' | 'brand'; children: ReactNode; mono?: boolean }) {
  return <span className={`chip ${kind ? `chip--${kind}` : ''} ${mono ? 'chip--mono' : ''}`}>{children}</span>;
}

export function fmtTime(ts: number): string {
  return new Date(ts).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}
